import { createSlice } from '@reduxjs/toolkit'
import api from 'api/axios/foka'
import { createAxiosThunk } from 'utils/reduxUtil'

const initialState = {
  channels: [],
  creatives: [],
  page: 0,
  size: 10,
  sort: 'createdDate',
  direction: 'desc',
  total: 0,
  activeCreativeSize: 0,
  checkedAll: false,
  isCreativesLoading: false,
  isChannelsLoading: false,
  error: null,
  deleteError: null,
  selectedIds: [],
  openDetailIds: [],
}

export const fetchActiveCreatives = createAxiosThunk(
  'creativeList/fetchActiveCreatives',
  (noArgs, { getState }) => {
    const {
      creativeListFilter: {
        filterReviewStatus,
        filterBizChannels,
        filterCreativeStatus,
      },
    } = getState().creative

    const creativeStatus = filterCreativeStatus
      .filter(status => status.checked)
      .map(status => status.id)
    const reviewStatus = filterReviewStatus
      .filter(status => status.checked)
      .map(status => status.name)
    const bizChannelIds = filterBizChannels
      .filter(channel => channel.checked)
      .map(channel => channel.id)

    if (creativeStatus.length === 1 && creativeStatus[0] === 'DELETED') {
      return { data: { creatives: { totalElements: 0 } } }
    }

    return api.creative.getCreatives({
      creativeStatus: ['ON'],
      reviewStatus,
      bizChannelIds,
      page: 0,
      size: 1,
    })
  }
)

export const fetchCreatives = createAxiosThunk(
  'creativeList/fetchCreatives',
  (noArgs, { getState }) => {
    const {
      creativeList: { page, size, sort, direction },
      creativeListFilter: {
        filterCreativeStatus,
        filterReviewStatus,
        filterBizChannels,
      },
    } = getState().creative

    const creativeStatus = filterCreativeStatus
      .filter(status => status.checked)
      .map(status => status.id)
    const reviewStatus = filterReviewStatus
      .filter(status => status.checked)
      .map(status => status.name)
    const bizChannelIds = filterBizChannels
      .filter(channel => channel.checked)
      .map(channel => channel.id)

    return api.creative.getCreatives({
      creativeStatus,
      reviewStatus,
      bizChannelIds,
      page,
      size,
      sort: `${sort} ${direction}`,
    })
  }
)

export const deleteSelectedCreatives = createAxiosThunk(
  'creativeList/deleteSelectedCreatives',
  (noArgs, { getState }) => {
    const {
      creativeList: { selectedIds },
    } = getState().creative
    return api.creative.deleteCreatives({ ids: selectedIds })
  }
)

const creativeListSlice = createSlice({
  name: 'creativeList',
  initialState,

  reducers: {
    reset: () => initialState,
    setPage: (state, { payload: page }) => {
      state.page = page
    },
    setSize: (state, { payload: size }) => {
      state.size = size
    },
    setSort: (state, { payload: { sort, direction } }) => {
      state.sort = sort
      state.direction = direction
    },
    setSelectedIds: (state, { payload }) => {
      state.selectedIds = payload
    },
    clearSelectedIds: state => {
      state.selectedIds = []
    },
    setOpenDetailIds: (state, { payload: id }) => {
      const index = state.openDetailIds.findIndex(e => e === id)
      if (index < 0) {
        state.openDetailIds.push(id)
      } else {
        state.openDetailIds.splice(index, 1)
      }
    },
  },

  extraReducers: builder => {
    builder.addCase(fetchCreatives.pending, state => {
      state.error = null
      state.isCreativesLoading = true
    })

    builder.addCase(fetchCreatives.fulfilled, (state, { payload }) => {
      const { content, totalElements } = payload.creatives
      state.creatives = content
      state.total = totalElements
      state.isCreativesLoading = false
      state.error = null
    })

    builder.addCase(fetchCreatives.rejected, (state, { payload }) => {
      state.error = payload || true
      state.isCreativesLoading = false
    })

    builder.addCase(deleteSelectedCreatives.pending, state => {
      state.loading = true
    })

    builder.addCase(deleteSelectedCreatives.fulfilled, state => {
      state.loading = false
    })

    builder.addCase(deleteSelectedCreatives.rejected, (state, { payload }) => {
      state.deleteError = payload
      state.loading = false
    })

    builder.addCase(fetchActiveCreatives.fulfilled, (state, { payload }) => {
      state.activeCreativeSize = payload.creatives.totalElements
    })
  },
})

export const {
  reset,
  setPage,
  setSize,
  setSelectedIds,
  clearSelectedIds,
  setOpenDetailIds,
  setSort,
} = creativeListSlice.actions

export default creativeListSlice.reducer
